import { HStack, VStack } from '@chakra-ui/react';
import React, { useEffect, useMemo } from 'react';
import CustomButton from '@/components/atoms/CustomButton';
import { useNavigate } from '@tanstack/react-router';
import colors from '@/theme/colors';
import * as Sentry from '@sentry/react';

export type TErrorBoundary = {
  code: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  success: boolean;
  errors: {
    message: string;
    code: number;
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload: any;
  }[];
  cause?: string;
  error?: { message: string };
};

type Props = {
  error: unknown;
  /**
   * Recover with a real page load instead of a client-side navigation. Set by the
   * full-page boundary, where the React tree above the error may itself be broken
   * and a router navigation would leave the fallback on screen.
   */
  hardReload?: boolean;
};

/**
 * The error message, the Sentry capture and the recovery action — with no page
 * template around it.
 *
 * Route-level `errorComponent`s replace the matched route's own component, so a
 * boundary on a route that sits inside the sidebar shell must render only the
 * body: SidebarContentWrapper is still rendering and supplies the chrome.
 *
 * ErrorBoundaryComponent is the full-page variant, used by the root boundary.
 */
const ErrorBody = ({ error, hardReload = false }: Props) => {
  const navigate = useNavigate();

  const finalError = useMemo(() => {
    // A route/render failure arrives as a real Error; an API rejection arrives as
    // the interceptor's TCustomAxiosError shape. Check the Error case first —
    // these boundaries now catch both, and the API shape has no `message`.
    if (error instanceof Error && error.message) return error.message;

    const apiError = error as TErrorBoundary;
    if (apiError?.errors?.[0]?.message) return apiError.errors[0].message;
    if (apiError?.error?.message) return apiError.error.message;
    return apiError?.cause;
  }, [error]);

  useEffect(() => {
    // Report the original value, not the derived string: Sentry can only group by
    // and recover a stack from the real Error. A string loses both.
    Sentry.captureException(error ?? new Error('Error caught by ErrorBody'));
  }, [error]);

  const goHome = () => {
    if (hardReload) {
      // Full page load, not navigate-then-reload: TanStack flushes the browser URL
      // asynchronously, so a reload could fire before '/' was committed and simply
      // reload the route that just failed.
      window.location.assign('/');
      return;
    }

    navigate({ to: '/' });
  };

  return (
    <VStack gap={'50px'}>
      <HStack>
        {<pre>Error: {finalError?.toString() ?? 'An error has occurred. Please reload the page or go back.'}</pre>}
      </HStack>
      <CustomButton
        backgroundColor={colors.primary[500]}
        hoverBackgroundColor={colors.primary[500]}
        width={'200px'}
        height={'50px'}
        labelColor="white"
        onClick={goHome}
        label="Home"
      />
    </VStack>
  );
};

export default ErrorBody;
